import { Box, Divider, Typography } from '@mui/material'
import React, { useMemo, useRef, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useTranslation } from '../../../../../common/hooks/helper/useTranslation'
import { useUserType } from '../../../../../common/hooks/useUserType'
import { AddAppointmentModal } from '../Appointment/AddAppointmentModal'
import { AppointmentDependencyModal } from '../Appointment/AppointmentDependencyModal'
import { AppointmentEmptyState } from '../Appointment/AppointmentEmptyState'
import { AppointmentModal } from '../Appointment/AppointmentModal'
import { AddSurveyModal, AddSurveyModalParams } from '../Survey/AddSurveyModal'
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined'
import { TreatmentLog } from './TreatmentLog'
import { TreatmentPanelHeader } from './TreatmentPanelHeader'
import { TreatmentPanelSubHeader } from './TreatmentPanelSubHeader'
import { TreatmentSurveyPreview } from '../Survey/TreatmentSurveyPreview'
import { AppointmentsWithArrows } from './AppointmentsWithArrows'
import {
  AddAppointmentModalParams,
  RouteParams,
  TreatmentPanelTab,
} from '../../types/treatmentPanel.types'
import { useSurveyOperations } from './hooks/useSurveyOperations'
import { AppointmentDeletedState } from '../Appointment/AppointmentDeletedState'
import { useAppointmentOperations } from './hooks/useAppointmentOperations'
import { useTreatmentGroupedAppointmentsComponentViewModel } from './hooks/useTreatmentGroupedAppointmentsComponentViewModel'
import { Survey } from '../../types/treatments.types'
import { EmptyState } from '../../../../../common/components/EmptyState'
import { ReferralDetailsModal } from '../../../DoctorPatients/components/PatientReferrals/ReferralDetailsModal'
import { DoctorPatientProfile } from '../../../DoctorPatients/types/doctorPatients.types'
import { useTreatmentPanelData } from './TreatmentPanelProvider'

interface Props {
  patientProfile: DoctorPatientProfile
}

export const TreatmentPanel: React.FC<React.PropsWithChildren<Props>> = ({
  patientProfile,
}) => {
  const {
    treatment,
    refetchTreatment,
    isTreatmentDeleted,
    isOwnTreatment,
    isAssistantOfTreatment,
    activeView,
    clearFilterValue,
    linkingAppointments,
    isAddReferralModalOpen,
    onCloseReferralModal,
    referralToShow,
  } = useTreatmentPanelData()
  const { t } = useTranslation()
  const history = useHistory()
  const { patientId, treatmentId, appointmentId, action } =
    useParams<RouteParams>()
  const userType = useUserType()

  const firstRef = useRef<HTMLElement>(null)

  const [survey, setSurvey] = useState<Survey | null>(null)
  const [addSurveyModal, toggleAddSurveyModal] = useState<AddSurveyModalParams>(
    { isOpen: false }
  )

  const [addAppointmentModalOpen, toggleAddAppointmentModal] =
    useState<AddAppointmentModalParams>({ isOpen: false })

  const isEditRoute: boolean = action === 'edit'
  const showLinkingModal = linkingAppointments.length === 2

  const appointments = useMemo(() => treatment.appointments || [], [treatment])

  const currentAppointment = appointments.find(
    (appointment) => appointment.id === appointmentId
  )

  const treatmentGroupedAppointmentsViewModel =
    useTreatmentGroupedAppointmentsComponentViewModel({
      setSurvey,
      firstRef,
      toggleAddAppointmentModal,
      toggleAddSurveyModal,
    })

  const { editAppointment, cloneAppointment } = useAppointmentOperations({
    toggleAddAppointmentModal,
  })

  const { onAddSurveyModalClose, onAddSaveSurveyModalSave } =
    useSurveyOperations({
      patientUserId: patientProfile.user?.id ?? '',
      setSurvey,
      toggleAddSurveyModal,
    })

  const showAppointmentEmptyState = useMemo(
    () => !isTreatmentDeleted && !appointments.length && isOwnTreatment,
    [isTreatmentDeleted, appointments, isOwnTreatment]
  )

  return (
    <Box height="100%" overflow="auto" display="flex">
      <Box width="100%">
        <Box height="100%" width="100%" display="flex" flexDirection="column">
          <TreatmentPanelHeader />

          <Divider />
          {!!appointments.length && (
            <TreatmentPanelSubHeader
              toggleAddSurveyModal={toggleAddSurveyModal}
              toggleAddAppointmentModal={toggleAddAppointmentModal}
            />
          )}

          {activeView === TreatmentPanelTab.TreatmentLog && <TreatmentLog />}

          {activeView === TreatmentPanelTab.Events && (
            <>
              {isTreatmentDeleted && <AppointmentDeletedState />}

              <>
                {showAppointmentEmptyState && (
                  <AppointmentEmptyState
                    toggleAddAppointment={() =>
                      toggleAddAppointmentModal({ isOpen: true })
                    }
                    toggleAddSurvey={() =>
                      toggleAddSurveyModal({ isOpen: true })
                    }
                  />
                )}

                {!isOwnTreatment && !isAssistantOfTreatment ? (
                  <EmptyState title={t('treatment:notMyTratment')} />
                ) : (
                  <AppointmentsWithArrows
                    treatmentGroupedAppointmentsViewModel={
                      treatmentGroupedAppointmentsViewModel
                    }
                  />
                )}

                {treatmentGroupedAppointmentsViewModel.isNoFilteredResultContent && (
                  <Box
                    display="flex"
                    height="100%"
                    alignItems="center"
                    justifyContent="center"
                    flexDirection="column"
                    mb={8}
                  >
                    <ListAltOutlinedIcon
                      sx={{ width: '100px', height: '100px', mb: 2 }}
                    />
                    <Typography variant="h4">{t('common:notFound')}</Typography>
                  </Box>
                )}
              </>
            </>
          )}
        </Box>
      </Box>

      <>
        {!!appointmentId && (
          <AppointmentModal
            appointment={currentAppointment}
            isOpen={!!appointmentId}
            editAppointment={editAppointment}
            cloneAppointment={cloneAppointment}
            onClose={() =>
              history.push(`/${userType}/patients/${patientId}/${treatmentId}`)
            }
          />
        )}

        {addAppointmentModalOpen.isOpen && (
          <AddAppointmentModal
            appointment={addAppointmentModalOpen.appointment}
            isOpen={addAppointmentModalOpen.isOpen}
            doctor={addAppointmentModalOpen.doctor}
            appointmentType={addAppointmentModalOpen.appointmentType}
            initProposedDate={addAppointmentModalOpen.proposedDate}
            onDone={() => {
              clearFilterValue()
              refetchTreatment()
            }}
            onClose={() => toggleAddAppointmentModal({ isOpen: false })}
          />
        )}

        {isEditRoute && (
          <AddAppointmentModal
            appointment={currentAppointment}
            isOpen={isEditRoute}
            onDone={() => {
              clearFilterValue()
              refetchTreatment()
            }}
            onClose={() => {
              history.push(
                `/${userType}/patients/${patientId}/${treatmentId}/${appointmentId}`
              )
              toggleAddAppointmentModal({ isOpen: false })
            }}
          />
        )}

        {showLinkingModal && (
          <AppointmentDependencyModal isOpen={showLinkingModal} />
        )}

        {addSurveyModal.isOpen && (
          <AddSurveyModal
            params={addSurveyModal}
            onSave={onAddSaveSurveyModalSave}
            onClose={onAddSurveyModalClose}
            title={
              !!addSurveyModal.survey
                ? t('treatment:doctor.modifySurveyDialogTitle')
                : t('treatment:doctor.assignSurveyDialogTitle')
            }
            submitButtonText={t('common:save')}
          />
        )}

        {!!survey?.surveySchema.id && (
          <TreatmentSurveyPreview
            isOpen={!!survey}
            surveySchemaId={survey?.surveySchema.id}
            surveyId={survey.id}
            close={() => setSurvey(null)}
          />
        )}

        {isAddReferralModalOpen && (
          <ReferralDetailsModal
            isOpen={isAddReferralModalOpen}
            handleModalClose={onCloseReferralModal}
            patientProfile={patientProfile}
            refetch={refetchTreatment}
            referralId={referralToShow?.id}
          />
        )}
      </>
    </Box>
  )
}
